/**
 * PLT-4755: API types for the batch SDG target reasoning endpoint.
 *
 * Lives in `common` (not `services/main`) so the frontend can import the
 * types directly without re-declaring them. The route handler lives in
 * `services/main/upserve/modules/sdg-alignments/`.
 */
import { SdgTarget } from './types';

/**
 * Mirrors `SdgReasoningDirection` from the backend Sequelize model so
 * frontend code can import this file without dragging in a server-only
 * dependency. Backend handler maps the two together at the validation
 * boundary.
 */
export enum SdgReasoningDirectionApi {
  POSITIVE = 'positive',
  NEGATIVE = 'negative',
}

/**
 * One `(productCode, sdgTarget, direction)` triple, as it appears in
 * either the request body or the `pending` list of the response.
 */
export interface SdgTargetReasoningKey {
  productCode: string;
  sdgTarget: SdgTarget;
  direction: SdgReasoningDirectionApi;
}

/**
 * One cached row returned in the `results` list. `generatedAt` is an
 * ISO-8601 string — Hapi serializes `Date` objects to strings, and the
 * frontend treats this as opaque (no client-side parsing required for v1).
 */
export interface SdgTargetReasoningResult extends SdgTargetReasoningKey {
  reasoning: string;
  generatedAt: string;
}

export interface BatchSdgTargetReasoningsRequest {
  items: SdgTargetReasoningKey[];
}

export interface BatchSdgTargetReasoningsResponse {
  results: SdgTargetReasoningResult[];
  pending: SdgTargetReasoningKey[];
}

/**
 * Maximum number of items per batch. Sized for the SDG lens of a single
 * company variant: ~30 products × up to ~10 surfaced targets × 2 directions
 * ≈ 600 worst case, but the lens groups by SDG and surfaces far fewer
 * targets at a time. 500 is a comfortable headroom; larger batches almost
 * certainly indicate a frontend bug (or a deliberate batch-warm-up that
 * should chunk instead).
 */
export const BATCH_SDG_TARGET_REASONINGS_MAX_ITEMS = 500;
